/**
 * URL manipulation and sanitization utilities.
 */

import reporter from './sentry';

/**
 * Allowed URL protocols for safe rendering in links.
 */
const SAFE_URL_PROTOCOLS = ['http:', 'https:', 'mailto:'];

/**
 * Checks if a URL is relative (no protocol).
 */
function isRelativeUrl(url: string): boolean {
  return !url.includes('://') && !url.startsWith('//');
}

/**
 * Appends search parameters to a URL.
 * Handles both absolute and relative URLs correctly.
 *
 * @param url - The URL to append parameters to
 * @param params - Object of key-value pairs to add as search params
 * @param baseUrl - Base URL for parsing relative URLs (e.g., 'https://example.com')
 * @returns URL with appended search parameters
 *
 * @example
 * appendSearchParamsToUrl('/page', { foo: 'bar' }, 'https://example.com') // '/page?foo=bar'
 * appendSearchParamsToUrl('https://example.com', { a: '1' }, 'https://example.com') // 'https://example.com?a=1'
 */
export function appendSearchParamsToUrl(
  url: string,
  params: Record<string, string>,
  baseUrl: string
): string {
  const isRelative = isRelativeUrl(url);
  const base = isRelative ? baseUrl : undefined;
  const urlObj = new URL(url, base);

  for (const [key, value] of Object.entries(params)) {
    if (value) {
      urlObj.searchParams.set(key, value);
    } else {
      urlObj.searchParams.delete(key);
    }
  }

  if (isRelative) {
    return urlObj.pathname + urlObj.search + urlObj.hash;
  }
  return urlObj.toString();
}

/**
 * Removes search parameters from a URL.
 *
 * @param url - The URL to remove parameters from
 * @param params - Array of parameter names to remove
 * @param baseUrl - Base URL for parsing relative URLs (e.g., 'https://example.com')
 * @returns URL with specified parameters removed
 */
export function removeSearchParamsFromUrl(url: string, params: string[], baseUrl: string): string {
  const isRelative = isRelativeUrl(url);
  const base = isRelative ? baseUrl : undefined;
  const urlObj = new URL(url, base);

  for (const param of params) {
    urlObj.searchParams.delete(param);
  }

  if (isRelative) {
    return urlObj.pathname + urlObj.search + urlObj.hash;
  }
  return urlObj.toString();
}

/**
 * Removes all search parameters that contain a given string from a URL (case-insensitive).
 *
 * @param url - The URL to remove parameters from
 * @param searchString - The string to match parameters against (e.g., "cursor")
 * @param baseUrl - Base URL for parsing relative URLs (e.g., 'https://example.com')
 * @returns URL with matching parameters removed
 *
 * @example
 * removeSearchParamsByPrefix('/page?cursor_a=1&cursor_b=2&filter=x', 'cursor', 'https://example.com')
 * // Returns: '/page?filter=x'
 */
export function removeSearchParamsByPrefix(url: string, searchString: string, baseUrl: string): string {
  const isRelative = isRelativeUrl(url);
  const base = isRelative ? baseUrl : undefined;
  const urlObj = new URL(url, base);
  const paramsToRemove: string[] = [];

  // Collect all parameter names that contain the search string (case-insensitive)
  for (const [paramName] of urlObj.searchParams) {
    if (paramName.toLowerCase().includes(searchString.toLowerCase())) {
      paramsToRemove.push(paramName);
    }
  }

  // Remove the collected parameters
  for (const param of paramsToRemove) {
    urlObj.searchParams.delete(param);
  }

  if (isRelative) {
    return urlObj.pathname + urlObj.search + urlObj.hash;
  }
  return urlObj.toString();
}

/**
 * Sanitizes a URL to prevent XSS attacks via dangerous protocols.
 * Only allows http:, https:, and mailto: protocols.
 * Returns '#' for unsafe or invalid URLs.
 *
 * @param url - The URL string to sanitize
 * @returns The sanitized URL or '#' if unsafe
 */
export function sanitizeHref(url: string): string {
  if (!url || typeof url !== 'string') {
    return '#';
  }

  const normalizedUrl = url.replace(/[\t\r\n]/g, '').trim();

  // Check for dangerous schemes at the start (case-insensitive)
  const lowercasedUrl = normalizedUrl.toLowerCase();
  if (
    lowercasedUrl.startsWith('javascript:') ||
    lowercasedUrl.startsWith('data:') ||
    lowercasedUrl.startsWith('vbscript:')
  ) {
    return '#';
  }

  // Allow relative URLs (paths, query strings, fragments)
  if (!normalizedUrl.includes(':') || normalizedUrl.startsWith('/') || normalizedUrl.startsWith('#')) {
    return normalizedUrl;
  }

  // For URLs with protocols, validate against safe list
  try {
    const parsed = new URL(normalizedUrl);
    if (SAFE_URL_PROTOCOLS.includes(parsed.protocol)) {
      return normalizedUrl;
    }
    return '#';
  } catch {
    // If URL parsing fails, check if it might be a relative URL with a port
    // e.g., "localhost:3000/path" - these should be allowed
    if (/^[a-zA-Z0-9.-]+:\d+/.test(normalizedUrl)) {
      return normalizedUrl;
    }
    return '#';
  }
}

/**
 * Converts a parameter name and value(s) into a query string format.
 * Handles both single values and arrays.
 *
 * @param param - The parameter name
 * @param value - Single string or array of strings
 * @returns Query string segment (e.g., "&param=value" or "&param[]=a&param[]=b")
 *
 * @example
 * stringsToQueryParams('filter', 'active') // '&filter=active'
 * stringsToQueryParams('ids', ['1', '2']) // '&ids[]=1&ids[]=2'
 */
export function stringsToQueryParams(param: string, value: string | string[]): string {
  return Array.isArray(value)
    ? value.map((val) => `&${param}[]=${encodeURIComponent(val)}`).join('')
    : `&${param}=${encodeURIComponent(value)}`;
}

/**
 * Sanitizes a URL, returning 'about:blank' for invalid URLs.
 * More strict than sanitizeHref - designed for programmatic URL handling.
 *
 * Pure: silently rewrites unsafe inputs to 'about:blank'. Use this where the
 * input is expected to occasionally be junk (user-entered URLs, query-param
 * redirect targets, internally-built URLs that may legitimately fall through
 * to a safe fallback). Use `sanitizeNavigationUrl` at navigation boundaries
 * where falling back to about:blank should be reported as a real bug.
 *
 * @param inputUrl - The URL to sanitize
 * @returns The sanitized URL or 'about:blank' if invalid
 */
export function sanitizeUrl(inputUrl: string): string {
  try {
    // Browsers strip tab/CR/LF and surrounding whitespace before scheme detection, so the check must see the same string the browser will.
    const normalizedUrl = inputUrl.replace(/[\t\r\n]/g, '').trim();
    const lowercasedUrl = normalizedUrl.toLowerCase();
    if (
      lowercasedUrl.startsWith('javascript:') ||
      lowercasedUrl.startsWith('data:') ||
      lowercasedUrl.startsWith('vbscript:')
    ) {
      return 'about:blank';
    }

    // Handle protocol-relative URLs
    if (normalizedUrl.startsWith('//')) {
      const url = new URL(`https:${normalizedUrl}`);
      return url.toString().substring(6);
    }

    // Check if URL is a relative path
    if (!normalizedUrl.includes('://')) {
      if (!normalizedUrl) {
        return 'about:blank';
      }

      if (normalizedUrl.includes('<') || normalizedUrl.includes('>')) {
        return 'about:blank';
      }

      return normalizedUrl;
    }

    // For absolute URLs, validate protocol
    const url = new URL(normalizedUrl);
    const allowedProtocols = ['http:', 'https:'];

    if (!allowedProtocols.includes(url.protocol)) {
      return 'about:blank';
    }
    return url.toString();
  } catch {
    return 'about:blank';
  }
}

/**
 * Sanitize a URL intended for navigation (router.push, router.replace,
 * window.history, redirect, <Link href>, window.open via openInNewTab). Wraps
 * `sanitizeUrl` and reports to the registered reporter (see `@bridge/utils/sentry`)
 * when an input is rewritten — bad navigation URLs are real bugs, not expected fallbacks.
 *
 * @param inputUrl - The URL to sanitize
 * @returns The sanitized URL or 'about:blank' if invalid
 */
export function sanitizeNavigationUrl(inputUrl: string): string {
  const result = sanitizeUrl(inputUrl);
  if (result === 'about:blank' && inputUrl !== 'about:blank') {
    reporter.captureException(new Error(`Invalid or unsafe URL sanitized: ${inputUrl}`));
  }
  return result;
}

/**
 * Opens a URL in a new tab via window.open, always sanitizing with sanitizeNavigationUrl
 * and passing noopener,noreferrer so the opened page gets no window.opener handle and no Referer.
 * Client-only.
 */
export function openInNewTab(url: string): void {
  window.open(sanitizeNavigationUrl(url), '_blank', 'noopener,noreferrer');
}
